import React, { useEffect } from 'react';
import { Field, FieldProps, Form, Formik } from 'formik';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    MenuItem,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useCourierActivate } from './useCourierActivate';
import FormikSelectControl from '../../atoms/FormikSelect/FormicSelectControl';
import FormikTextField from '../../atoms/FormikInput/FormikInput';
import FormikDatePicker from '../../atoms/FormikDatePicker/FormikDatePicker';
import FormikMaskedInput from '../../atoms/FormikMaskedInput/FormikMaskedInput';
import { Courier } from '../../../api/couriers';
import Spin from '../../atoms/Spin/Spin';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        mt2: {
            marginTop: theme.spacing(3),
        },
    })
);

interface Props {
    id?: number | string;
    courier?: Courier;
    open: boolean;
    load: () => void;
    onClose: () => void;
}

export const CourierEditDialog = ({ id, load, courier, open, onClose }: Props) => {
    const classes = useStyles();

    const {
        courierForm,
        courierFormValidScheme,
        handleUpdate,
        courierFormOpen,
        setCourierFormOpen,
        documentTypes,
        handleEditButton,
        courierLoading,
    } = useCourierActivate();

    useEffect(() => {
        if (open) {
            handleEditButton(courier, id);
        }
    }, [courier, handleEditButton, id, open]);

    return (
        <Dialog
            open={courierFormOpen}
            fullWidth={true}
            onClose={() => {
                setCourierFormOpen(false);
                onClose();
            }}
        >
            <Formik
                enableReinitialize={true}
                initialValues={courierForm}
                validationSchema={courierFormValidScheme}
                onSubmit={(values, { setSubmitting }) => {
                    handleUpdate(values, setSubmitting).then(() => {
                        load();
                        onClose();
                    });
                }}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <DialogTitle>Данные курьера</DialogTitle>
                        <DialogContent style={{ position: 'relative' }}>
                            {courierLoading && <Spin />}
                            <Box display="flex" flexDirection="column" position="relative">
                                <Field name="document_type.id">
                                    {({ ...props }) => (
                                        <FormikSelectControl
                                            cls={classes.mt2}
                                            label="Вид документа"
                                            labelId="doc"
                                            {...props}
                                        >
                                            {documentTypes.map((doc, index) => (
                                                <MenuItem key={index} value={doc.id}>
                                                    {doc.description}
                                                </MenuItem>
                                            ))}
                                        </FormikSelectControl>
                                    )}
                                </Field>
                                <Field
                                    className={classes.mt2}
                                    component={FormikTextField}
                                    name="passport_number"
                                    label="Номер документа"
                                />
                                <Field name="passport_date">
                                    {({ ...props }: FieldProps) => (
                                        <FormikDatePicker
                                            className={classes.mt2}
                                            label="Дата выдачи"
                                            inputVariant="standard"
                                            clearable
                                            disableToolbar={false}
                                            {...props}
                                        />
                                    )}
                                </Field>
                                <Field
                                    className={classes.mt2}
                                    component={FormikTextField}
                                    name="passport_issued_by"
                                    label="Кем выдано"
                                />
                                <Field
                                    className={classes.mt2}
                                    component={FormikMaskedInput}
                                    name="card_number"
                                    mask="KZ******************"
                                    label="Номер счета карты (IBAN)"
                                />
                                <Field
                                    className={classes.mt2}
                                    component={FormikTextField}
                                    name="fact_address"
                                    label="Фактический адрес проживания"
                                />
                            </Box>

                            {isSubmitting && <LinearProgress />}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose}>Отмена</Button>
                            <Button type={'submit'}>Ок</Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default CourierEditDialog;
